"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import ImageTile from "../../../atoms/imageTile";
import {
  CALENDAR,
  COUNTRIES,
  CURRENCY_ID,
  DELIVERY_METHOD,
  SELECT_DELIVERY_DATE,
  SELECT_SHIPPING_METHOD_AND_TIME_SLOT,
} from "../../../../constants/common/common";
import Calendar from "../../../molecules/calendar/calendar";
import ShippingMethods from "../../../molecules/shippingMethods/shippingMethods";
import {
  requestToFetchGetDeliveryDateAndGeoPriceAction,
  setDeliveryDetailsAction,
} from "../../../../store/slices/common/shippingDateAndSlotSelection";
import ButtonComponent from "../../../atoms/button";
import RedirectionToPLPComponent from "../../../../containers/productDetailPage/productDetailComponents/redirectionToPLPComponent";
import { getOutOfStock } from "../../../../utils/pdp/nonDeliverable";
import { setGaDateSelection } from "../../../../analytics/gaEvents";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import useAnalytics from "../../../../analytics/useAnalytics";
import { GA4_EVENTS } from "../../../../analytics/gaConstant";
import { parseDate, processDeliveryDate } from "../../../../utils/common";
import useCleverTapCartData from "../../../../customHooks/clevertap/cart";
import { getMultiOrderDateRange } from "../../../../utils/pdp/common";
import SerenadeProduct from "../../../molecules/serenadeProduct";
import { isDateTodayOrTomorrow } from "../../../../utils/common/deliveryDate";

/**
 * Represents the DomesticShippingDateAndSlotSelection component for selecting the date, shipping method, and time slot.
 *
 * This component allows the user to select a delivery date, choose a shipping method, and select a time slot for
 * domestic shipping. It handles navigation between the calendar and shipping method selection components.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onToggleDeliveryDetailsPopUp - Callback function that is triggered when the user wants to
 *   toggle the visibility of the delivery details popup.
 * @param {Object} props.currentItem - The item being shipped, containing information such as catalog ID, stock
 *   availability, etc.
 * @param {string} [props.classes=""] - Additional classes to apply to the component's wrapper. Defaults to an empty string.
 * @param {boolean} [props.isCart=false] - Flag indicating whether the component is being used in the cart context.
 *   Defaults to `false`.
 * @param {Object} props.deliveryDates - The available delivery dates for the item, including details such as blocked dates.
 * @param {Object} props.blockedDates - Details about blocked dates for delivery, including any catalog content
 *   restrictions or special messages.
 * @param {boolean} props.isCutoffTimestamp - Flag indicating whether there is a cutoff timestamp for delivery selection.
 * @param {Object} props.msgContent - Contains message content for special catalog messages (e.g., same-day delivery)
 *   or other relevant delivery information.
 * @param {string} props.pincode - The pincode for the shipping location to determine available shipping methods.
 * @param {boolean} props.isMobile - Flag indicating whether the component is being viewed on a mobile device.
 * @param {string} props.selectedCurrency - The selected currency for the transaction.
 *
 * @returns {React.ReactElement} A React element rendering the shipping date and slot selection UI, including
 *   the calendar and shipping methods components.
 */
const DomesticShippingDateAndSlotSelection = ({
  onToggleDeliveryDetailsPopUp,
  currentItem,
  classes,
  isCart = false,
  deliveryDates,
  blockedDates,
  isCutoffTimestamp,
  msgContent,
  pincode,
  isMobile,
  selectedCurrency,
  popupCleverTapData,
  cleverTapData,
  cartCleverTapData,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const updatedCleverTapData = useRef(null);
  const selectedDeliveryDetails = useSelector((state) => state?.shippingDateAndSlotSelection.selectedDeliveryDetails);

  const [selectedDate, setSelectedDate] = useState("");
  const [currentShippingMethod, setCurrentShippingMethod] = useState({});
  const [isSameDateSelected, setIsSameDateSelected] = useState(false);
  const [multiOrderProductChange, setMultiOrderProductChange] = useState(false);
  const [isSameShippingSelected, setIsSameShippingSelected] = useState(false);
  const [nonDeliverableToast, setNonDeliverableToast] = useState(false);
  const [userSelectedTimeSlotId, setUserSelectedTimeSlotId] = useState(null);
  const clientVariantType = useSelector((state) => state?.pdp?.clientVariantType);
  const nextAvailableDifference = useSelector((state) => state?.shippingDateAndSlotSelection?.nextAvailableDifference);
  const { isDateToday, isDateTomorrow } = isDateTodayOrTomorrow(new Date(currentItem?.deliveryDate));

  const [currentComponent, setCurrentComponent] = useState(CALENDAR);
  const [multiOrderDateRange, setMultiOrderDateRange] = useState([]);
  const isOutOfStock = getOutOfStock(currentItem?.productStockAvailable, currentItem?.productId);
  const { isMultiOrderProduct, multiOrderProductDetails } = currentItem;
  const domesticDeliveryDates = deliveryDates?.deliveryDates;

  const cleverTapHooksData = useCleverTapCartData({
    currentItem,
    clientVariantType,
    nextAvailableDifference,
    isSameDateSelected,
    isSameShippingSelected,
    userSelectedTimeSlotId,
    isDateToday,
    isDateTomorrow,
  });

  const clevertapDeliveryData = {
    ...cleverTapHooksData,
    ...cartCleverTapData,
  };
  const pdpClevertapDeliveryData = {
    ...popupCleverTapData,
    isDateToday,
    isDateTomorrow,
  };

  /**
   * This function for handling click on the drawer back button.
   *
   * @function
   * @returns {void}
   */
  const onHandleBackButtonClick = () => {
    switch (currentComponent) {
      case DELIVERY_METHOD:
        setCurrentComponent(CALENDAR);
        break;

      case CALENDAR:
        onToggleDeliveryDetailsPopUp();
        break;

      default:
        break;
    }
  };

  /**
   * Function to get the header title for the delivery details process.
   *
   * @function
   * @returns {string} The header title for the delivery details process.
   */
  const headerTitleForDeliveryDetails = () => {
    switch (currentComponent) {
      case CALENDAR:
        return SELECT_DELIVERY_DATE;
      case DELIVERY_METHOD:
        return SELECT_SHIPPING_METHOD_AND_TIME_SLOT;
    }
  };

  /**
   * Function to toggle the delivery details pop-up and track the event when it is closed.
   * It calls the `onToggleDeliveryDetailsPopUp` function to change the state of the pop-up
   * and then triggers a CleverTap event to record the closure of the pop-up.
   *
   * @function
   * @returns {void} This function does not return any value.
   */
  const onHandleDeliveryDetailsPopupToggle = () => {
    onToggleDeliveryDetailsPopUp();
    trackEvent(
      cleverTapCommonEvents.deliveryPopupClosed,
      {
        ...pdpClevertapDeliveryData,
        date_of_delivery: processDeliveryDate(selectedDate) || "",
        delivery_shipping: currentShippingMethod?.shippingMethodName,
        delivery_shipping_fee: "",
        delivery_slot: "",
      },
      ["clevertap"],
    );
  };

  /**
   * Handles the selection of a date in the calendar.
   * After selecting a date, it sets the selected date in state and moves the current component to the delivery method.
   *
   * @function
   * @param {string} date - The selected date from the calendar.
   * @returns {void}
   */
  const onDateSelection = (date) => {
    setSelectedDate(date);
    if (isMultiOrderProduct) {
      const lastDeliverableMultiOrderDate =
        domesticDeliveryDates[Number(domesticDeliveryDates?.length) - Number(multiOrderProductDetails?.noOfDays)];
      if (parseDate(lastDeliverableMultiOrderDate) < parseDate(date)) {
        setNonDeliverableToast(parseDate(lastDeliverableMultiOrderDate) < parseDate(date));
        setMultiOrderDateRange([]);
        setMultiOrderProductChange(false);
      } else {
        setNonDeliverableToast(false);
        setMultiOrderProductChange(true);
        setMultiOrderDateRange(getMultiOrderDateRange(date, multiOrderProductDetails));
      }
    }

    trackEvent(
      GA4_EVENTS?.DELIVERY_DATE_SELECTION,
      {
        ...setGaDateSelection(date, "pdp_interaction", GA4_EVENTS?.DELIVERY_DATE_SELECTION),
      },
      ["ga4"],
    );

    if (isCart) {
      setIsSameDateSelected(selectedDate === currentItem?.deliveryDate);
      updatedCleverTapData.current = {
        ...clevertapDeliveryData,
        date_of_delivery: processDeliveryDate(date),
        delivery_shipping: "",
        delivery_shipping_fee: "",
        delivery_slot: "",
      };
    } else {
      updatedCleverTapData.current = {
        ...pdpClevertapDeliveryData,
        date_of_delivery: processDeliveryDate(date),
        delivery_shipping: "",
        delivery_shipping_fee: "",
        delivery_slot: "",
      };
    }
    trackEvent(cleverTapCommonEvents.deliveryDateSelected, updatedCleverTapData.current, ["clevertap"]);
  };

  useEffect(() => {
    if (selectedDeliveryDetails?.selectedDate) {
      onDateSelection(selectedDeliveryDetails?.selectedDate);
    }
  }, [selectedDeliveryDetails?.selectedDate]);

  /**
   * Handles the selection of time and shipping method.
   * Combines the selected time and shipping method with the selected date, and calls the onHandleDeliveryDetailsClick callback.
   *
   * @function
   * @param {Object} timeNShippingMethod - An object containing the selected time slot and shipping method.
   * @returns {void}
   */
  const onTimeAndShippingMethodSelection = (timeNShippingMethod) => {
    dispatch(setDeliveryDetailsAction({ ...timeNShippingMethod, selectedDate }));
    const queryParams = {
      productId: currentItem?.productId,
      pinCode: pincode,
      currencyUomId: CURRENCY_ID,
      deliveryDate: selectedDate,
      catalogId: currentItem?.catalogId,
    };
    dispatch(requestToFetchGetDeliveryDateAndGeoPriceAction(queryParams));
    onToggleDeliveryDetailsPopUp(timeNShippingMethod, selectedDate);
  };

  return (
    <div className={`w-[100%] ${classes} flex flex-col gap-16`}>
      <div className="flex items-center justify-start gap-16 border-b-1 border-[#F5F5F5] px-5 py-16">
        <div className={`flex w-full ${isMobile ? "gap-12" : isCart ? "gap-16" : "justify-between"} items-center`}>
          <div className="cursor-pointer" onClick={onHandleBackButtonClick}>
            <ImageTile imgSrc={"/icons/left-arrow.svg"} imgHeight={20} imgWidth={20} imgAlt={"left-side-arrow"} />
          </div>
          <div className="top-2 text-16 font-600 text-black-100" data-testid="cart">
            {headerTitleForDeliveryDetails()}
          </div>
          {!isMobile && !isCart && (
            <div className="cursor-pointer">
              <ButtonComponent
                buttonVariant="icon"
                icon="/icons/close-circle-black.svg"
                onClick={onHandleDeliveryDetailsPopupToggle}
                classes="border-0"
                size="large"
              />
            </div>
          )}
        </div>
      </div>
      <div
        className={`custom-scrollbar container mx-auto ${isMultiOrderProduct ? "" : "h-[75vh]"} overflow-x-hidden px-20 pb-20 md:h-full`}
      >
        {currentComponent === CALENDAR && (
          <>
            <Calendar
              onDateSelection={(date) => {
                onDateSelection(date);
                if (!isMultiOrderProduct) {
                  setCurrentComponent(DELIVERY_METHOD);
                }
              }}
              selectedDate={selectedDate}
              isMultiOrderProduct={isMultiOrderProduct}
              multiOrderDateRange={multiOrderDateRange}
            />
            {currentItem?.catalogId === COUNTRIES.INDIA &&
              !isOutOfStock &&
              deliveryDates?.deliveryDates?.length > 0 && (
                <>
                  <RedirectionToPLPComponent
                    blockedDates={blockedDates?.deliveryBlockDates || []}
                    isDisplayCatalogContent={blockedDates?.displayCatalogContent}
                    isCutoffTimestamp={isCutoffTimestamp}
                    isCalender={false}
                    deliveryDatesList={deliveryDates?.deliveryDates}
                    samedayMsgContent={msgContent}
                    limitedCatalogContent={blockedDates?.content}
                    pincode={pincode}
                    cleverTapData={cleverTapData}
                  />
                </>
              )}
            {isMultiOrderProduct && (
              <SerenadeProduct
                multiOrderProductChange={multiOrderProductChange}
                isMultiOrderProduct={isMultiOrderProduct}
                nonDeliverableToast={nonDeliverableToast}
                multiOrderProductDetails={multiOrderProductDetails}
                setCurrentComponent={setCurrentComponent}
                setNonDeliverableToast={setNonDeliverableToast}
                selectedDate={selectedDate}
              />
            )}
          </>
        )}
        {currentComponent === DELIVERY_METHOD && (
          <ShippingMethods
            currentItem={currentItem}
            onTimeAndShippingMethodSelection={onTimeAndShippingMethodSelection}
            selectedDate={selectedDate}
            currentShippingMethod={currentShippingMethod}
            setCurrentShippingMethod={setCurrentShippingMethod}
            selectedCurrency={selectedCurrency}
            isInternational={false}
            showTimeSlots={currentComponent === DELIVERY_METHOD}
            popupCleverTapData={updatedCleverTapData.current}
            cartCleverTapData={updatedCleverTapData.current}
            isCart={isCart}
            setIsSameShippingSelected={setIsSameShippingSelected}
            setUserSelectedTimeSlotId={setUserSelectedTimeSlotId}
          />
        )}
      </div>
    </div>
  );
};
DomesticShippingDateAndSlotSelection.propTypes = {
  onToggleDeliveryDetailsPopUp: PropTypes.func.isRequired,
  currentItem: PropTypes.object.isRequired,
  classes: PropTypes.string,
  isCart: PropTypes.bool,
  deliveryDates: PropTypes.object,
  blockedDates: PropTypes.object,
  isCutoffTimestamp: PropTypes.bool,
  isMobile: PropTypes.bool,
  msgContent: PropTypes.object,
  pincode: PropTypes.string,
  selectedCurrency: PropTypes.object,
  popupCleverTapData: PropTypes.object,
  cleverTapData: PropTypes.object,
  cartCleverTapData: PropTypes.object,
};
export default DomesticShippingDateAndSlotSelection;
